import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { KAL_DIALOG_DATA, KalDialogConfig, KalDialogRef, KalDialogService } from '@kalidea/kaligraphi';
import isNil from 'lodash-es/isNil';
import { filter, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import {
    SharedAdvancedRuleLinearFormulaDialogComponent,
    SharedAdvancedRuleLinearFormulaDialogData
} from 'src/app/_shared/dialogs/shared-advanced-rules/shared-advanced-rule-linear-formula-dialog/shared-advanced-rule-linear-formula-dialog.component';
import {
    AdvancedRuleService,
    ComputationNodeType,
    CritereModel,
    RuleComputation,
    TypesOperateur
} from 'src/app/_shared/services/advanced-rule/advanced-rule.service';
import { arrayEmpty, arrayOnlyOne } from 'src/app/utils/array.utils';
import { MoteurOperateurDto, ParametreRegleAvanceeModelDto } from 'src/graphql/generated-types';

export interface SharedAdvancedRuleComputationsDialogData {
    ruleComputationList: RuleComputation[];

    accepted?: boolean;
}

@Component({
    selector: 'app-shared-advanced-rule-computations-dialog',
    templateUrl: './shared-advanced-rule-computations-dialog.component.html',
    styleUrls: ['./shared-advanced-rule-computations-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAdvancedRuleComputationsDialogComponent extends BaseComponent implements OnInit {

    form: UntypedFormGroup;

    errors: string[] = [];

    ruleComputationList: RuleComputation[] = [];

    selectedNode: RuleComputation = null;

    ComputationNodeType = ComputationNodeType;

    criteriaList: CritereModel[] = [];

    readonly operatorList = [
        {value: '(', label: '('},
        {value: ')', label: ')'},
        {value: '+', label: '+'},
        {value: '-', label: '-'},
        {value: '*', label: '*'},
        {value: '/', label: '/'},
        {value: 'Minimum', label: 'Min'},
        {value: 'Maximum', label: 'Max'},
    ];

    operatorMap: Record<string, MoteurOperateurDto> = {};

    parameterList: ParametreRegleAvanceeModelDto[] = [];

    parameterCriterionSubscription: Subscription = Subscription.EMPTY;

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly advancedRuleService: AdvancedRuleService,
                private readonly fb: UntypedFormBuilder,
                private readonly dialogService: KalDialogService,
                private readonly dialogRef: KalDialogRef<SharedAdvancedRuleComputationsDialogComponent>,
                @Inject(KAL_DIALOG_DATA) public readonly data: SharedAdvancedRuleComputationsDialogData) {
        super(cdr);
    }

    get shouldDisplayParams(): boolean {
        const criterion = this.form.get('criterion').value;
        return !!criterion?.PossedeDesParametres;
    }

    get isValid(): boolean {
        return arrayEmpty(this.errors);
    }

    close(): void {
        this.dialogRef.close({accepted: false});
    }

    submit(): void {
        this.dialogRef.close({
            accepted: true,
            ruleComputationList: this.ruleComputationList
        });
    }

    selectNode(node: RuleComputation): void {
        this.selectedNode = node;

        if (node.type === ComputationNodeType.Critere && node.Critere.PossedeDesParametres) {
            this.form.patchValue({
                parametersList: node.Parametres,
                criterion: node.Critere
            }, {emitEvent: false});

            this.parameterCriterionSubscription.unsubscribe();

            this.parameterCriterionSubscription = this.form.get('parametersList').valueChanges.pipe(
                this.untilDestroyed,
                tap(parameters => node.Parametres = parameters)
            ).subscribe();
        }
    }

    removeNode(): void {
        if (!this.selectedNode) {
            return;
        }
        const nodeIndex = this.ruleComputationList.indexOf(this.selectedNode);
        this.ruleComputationList.splice(nodeIndex, 1);

        if (arrayEmpty(this.ruleComputationList)) {
            this.selectedNode = null;
            return;
        }

        this.selectedNode = this.ruleComputationList[nodeIndex < this.ruleComputationList.length ? nodeIndex : this.ruleComputationList.length - 1];

        this.checkErrors();
    }

    addComputationNode({
                           node,
                           isAfter
                       }: { node: RuleComputation, isAfter: boolean }): void {

        if (!this.selectedNode) {
            this.ruleComputationList.push(node);
            return;
        }

        const index = this.ruleComputationList.indexOf(this.selectedNode) + (isAfter ? 1 : 0);
        if (this.ruleComputationList.length > index) {
            this.ruleComputationList.splice(index, 0, node);
            return;
        }

        this.ruleComputationList.push(node);
        this.checkErrors();
    }

    addOperator(value: string): void {
        const node = {Operateur: this.operatorMap[value], type: ComputationNodeType.Operateur};
        this.addComputationNode({
            node,
            isAfter: true
        });

        this.selectedNode = node;

        this.checkErrors();
    }

    addCriterion({isAfter}: { isAfter: boolean }): void {
        const parametersControl = this.form.get('parametersList');
        const criterion = this.form.get('criterion').value;
        const parameters = parametersControl.value;
        if (!criterion) {
            return;
        }

        const node = {Critere: criterion, Parametres: parameters, type: ComputationNodeType.Critere};
        this.addComputationNode({
            node,
            isAfter
        });

        this.parameterCriterionSubscription.unsubscribe();

        this.parameterCriterionSubscription = parametersControl.valueChanges.pipe(
            this.untilDestroyed,
            tap(parameters => node.Critere.Parametres = parameters)
        ).subscribe();

        this.selectedNode = node;
    }

    addValue({isAfter}: { isAfter: boolean }): void {
        const value = this.form.get('value').value;
        if (isNil(value)) {
            return;
        }

        const node = {Valeur: value, type: ComputationNodeType.Valeur};
        this.addComputationNode({node, isAfter});
        this.selectedNode = node;

        this.checkErrors();
    }

    openLinearFormulaDialog(): void {
        const config = new KalDialogConfig<SharedAdvancedRuleLinearFormulaDialogData>({
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            title: 'Formule de calcul linéaire',
        });

        this.watch$ = this.dialogService.open(SharedAdvancedRuleLinearFormulaDialogComponent, config).afterClosed.pipe(
            filter(r => !!r?.accepted),
            tap<SharedAdvancedRuleLinearFormulaDialogData>(({computationList, replaceCurrentComputations}) => {
                this.parameterCriterionSubscription.unsubscribe();
                const index = this.ruleComputationList.indexOf(this.selectedNode) + 1;
                this.selectedNode = null;

                if (replaceCurrentComputations) {
                    this.ruleComputationList = computationList;
                } else {
                    this.ruleComputationList.splice(index, 0, ...computationList);

                }
                this.checkErrors();
                this.cdr.markForCheck();
            })
        );
    }

    private checkErrors(): void {
        this.errors = [];

        if (arrayEmpty(this.ruleComputationList)) {
            return;
        }

        if (arrayOnlyOne(this.ruleComputationList)) {
            const node = this.ruleComputationList[0];

            if (node.type === ComputationNodeType.Operateur) {
                this.errors.push('Si vous n\'avez qu\'un élément, il doit s\'agir d\'un critère ou d\'une valeur.');
                return;
            }

            if (node.type === ComputationNodeType.Valeur && node.Valeur < 0) {
                this.errors.push('La valeur doit être supérieur ou égale à 0');
                return;
            }

            return;
        }

        if (this.ruleComputationList.length === 2) {
            this.errors.push('Un calcul avancé doit être composé d\'un critère ou d\'une valeur ou au moins" 3 éléments : critère, opérateur, valeur');
            return;
        }

        let parenthesisIntegrity = 0;

        this.ruleComputationList.forEach((node, index) => {

            if (index === 0) {
                if (node.type !== ComputationNodeType.Operateur) {
                    return;
                }

                if (node.Operateur.Id > 0) {
                    this.errors.push('Un calcul ne peut pas commencer par un opérateur.');
                    return;
                }

                if (node.Operateur.Libelle === ')') {
                    this.errors.push('Un calcul ne peut commencer par une parenthèse fermante.');
                }

                if (node.Operateur.Libelle === '(') {
                    parenthesisIntegrity += 1;
                }

                return;
            }

            const previousNode = this.ruleComputationList[index - 1];

            if (node.type === ComputationNodeType.Critere) {

                if (previousNode.Operateur?.Libelle === ')') {
                    this.errors.push('Impossible d\'avoir une parenthèse fermante suivit d\'un critère.');
                    return;
                }
                if (previousNode.type === ComputationNodeType.Critere) {
                    this.errors.push('Impossible d\'avoir un critère suivit d\'un critère.');
                    return;
                }
                if (previousNode.type === ComputationNodeType.Valeur) {
                    this.errors.push('Impossible d\'avoir une valeur suivit d\'un critère.');
                }
                return;
            }

            if (node.type === ComputationNodeType.Valeur) {

                if (previousNode.Operateur?.Libelle === ')') {
                    this.errors.push('Impossible d\'avoir une parenthèse fermante suivit d\'une valeur.');
                    return;
                }
                if (previousNode.type === ComputationNodeType.Critere) {
                    this.errors.push('Impossible d\'avoir une valeur suivit d\'une valeur.');
                    return;
                }
                if (previousNode.type === ComputationNodeType.Valeur) {
                    this.errors.push('Impossible d\'avoir une valeur suivit d\'une valeur.');
                }
                return;
            }

            // if operateur
            if (node.Operateur.Id > 0) {
                if (previousNode.type !== ComputationNodeType.Operateur) {
                    return;
                }
                if (previousNode.Operateur.Libelle === '(') {
                    this.errors.push('Impossible d\'avoir une parenthèse ouvrante suivit d\'un opérateur.');
                    return;
                }
                if (previousNode.Operateur.Id > 0) {
                    this.errors.push('Impossible d\'avoir un opérateur suivit d\'un opérateur.');
                }
                return;
            }
            if (node.Operateur.Libelle === '(') {
                parenthesisIntegrity += 1;

                if (previousNode.Operateur?.Libelle === ')') {
                    this.errors.push('Impossible d\'avoir une parenthèse fermante suivit d\'une parenthese ouvrante.');
                    return;
                }

                if (previousNode.type === ComputationNodeType.Critere) {
                    this.errors.push('Impossible d\'avoir un critère suivit d\'une parenthese ouvrante.');
                    return;
                }
                if (previousNode.type === ComputationNodeType.Valeur) {
                    this.errors.push('Impossible d\'avoir une valeur suivit d\'une parenthese ouvrante.');
                }
                return;
            }
            // node.Operateur.Libelle === ')'
            parenthesisIntegrity -= 1;
            if (previousNode.type !== ComputationNodeType.Operateur) {
                return;
            }
            if (previousNode.Operateur.Libelle === '(') {
                this.errors.push('Impossible d\'avoir une parenthèse ouvrante suivit d\'une parenthèse fermante.');
                return;
            }
            if (previousNode.Operateur.Id > 0) {
                this.errors.push('Impossible d\'avoir un opérateur suivit d\'une parenthèse fermante.');
            }
            return;
        });

        if (parenthesisIntegrity !== 0) {
            const isMissingOpenParenthesis = parenthesisIntegrity < 0;
            parenthesisIntegrity = isMissingOpenParenthesis ? -parenthesisIntegrity : parenthesisIntegrity;
            this.errors.push(`Il manque ${parenthesisIntegrity} parenthèses ${isMissingOpenParenthesis ? 'ouvrante' : 'fermante'}.`);
        }
    }

    private initSubscriptions(): void {
        const {criterion: criterionControl, parametersList: parametersListControl} = this.form.controls;

        this.watch$ = criterionControl.valueChanges.pipe(
            tap(() => {
                this.parameterCriterionSubscription.unsubscribe();
                parametersListControl.patchValue([], {emitEvent: false});
                this.cdr.markForCheck();
            })
        );
    }

    private initData(): void {
        this.ruleComputationList = [...this.data.ruleComputationList]; // maybe clonedeep, see with params
        this.operatorMap = this.operatorList.reduce<Record<string, MoteurOperateurDto>>((record, {value}) => {
            record[value] = this.advancedRuleService.operatorList.find(({Libelle}) => Libelle === value);

            if (['(', ')'].includes(value) && !record[value]) {
                record[value] = {Libelle: value, Valeur: value, TypeOperateur: TypesOperateur.Arithmetique, Id: 0};
            }

            return record;
        }, {});

        this.criteriaList = this.advancedRuleService.criteriaOfCriteriaList;

        this.parameterList = this.advancedRuleService.ruleParamList;
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            criterion: [null],
            parametersList: [[]],
            value: [null],
        });

        this.initData();

        this.initSubscriptions();
    }
}
